/* eslint-disable quotes */
/* eslint-disable indent */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Spin,
  DatePicker,
  Button,
  message,
  Card,
  Row,
  Col,
  Typography,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import API from "../../../api";
import urls from "../../../api/urls";
import moment from "moment";
import { dateFormat_dmy } from "../../../utils";
import Heading from "../../../components/Heading";
import TableContainer from "../../../components/TableContainer";
import { generateDynamicColumns } from "../../../utils/columnGenerator";
import useReportFilters from "../../../hooks/useReportFilters";
import { FilterOutlined, FileTextOutlined } from "@ant-design/icons";
import constants from "../../../constants/constants";
import "./style.scss";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const {
  DRS_STATUS_REPORT_2,
  DRS_STATUS_DELIVERY_AGENT_LIST,
  DRS_STATUS_CHANNEL_PARTNER_LIST,
  DRS_STATUS_REPORT_DOWNLOAD,
} = urls;
const { Option } = Select;

const disabledDate = (current) => {
  return current && current > moment().endOf("day");
};

const DRSStatusReport = () => {
  const {
    filters,
    debouncedFilters,
    handleFilterChange,
    handlePageChange,
    pageNum,
    pageSize,
    setPageNum,
  } = useReportFilters({
    cp_id: undefined,
    delivery_agent_id: undefined,
  }, 500, 0);

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState("-");
  const [returnedRecords, setReturnedRecords] = useState("-");
  const [dateRange, setDateRange] = useState({
    startDate: moment(),
    endDate: moment(),
  });
  const [channelPartners, setChannelPartners] = useState([]);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [refreshSchedule, setRefreshSchedule] = useState(null);

  const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
  const userRole = userinfo?.roles?.[0];
  const isCPUser = [
    constants.ROLES.CP_ADMIN,
    constants.ROLES.CP_OPERATOR,
    constants.ROLES.DP_OPERATOR,
  ].includes(userRole);
  const getPartnerIdInfo = () => {
    if (userRole === constants.ROLES.CP_ADMIN || userRole === constants.ROLES.CP_OPERATOR) {
      return { key: "cpId", downloadKey: "cpId", value: userinfo?.spId || null };
    } else if (userRole === constants.ROLES.DP_OPERATOR) {
      const deliveryPartners = JSON.parse(sessionStorage.getItem("deliveryPartner") || "{}")?.data || [];
      const dpId = deliveryPartners.length > 0 ? deliveryPartners[0]?.dpId : null;
      return { key: "dp_id", downloadKey: "dpId", value: dpId };
    }
    return { key: "cpId", downloadKey: "cpId", value: null };
  };
  const partnerInfo = getPartnerIdInfo();

  const fetchReportData = async (page, size) => {
    setLoading(true);
    try {

      const queryParamsObject = {
        fromDate_drs_creation_date: dateRange.startDate.format("YYYY-MM-DD"),
        toDate_drs_creation_date: dateRange.endDate.format("YYYY-MM-DD"),
        page: page,
        size: size,
      };

      if (isCPUser && partnerInfo.value) {
        queryParamsObject[partnerInfo.key] = partnerInfo.value;
      } else if (debouncedFilters.cp_id) {
        queryParamsObject.cpId = debouncedFilters.cp_id;
      }

      if (debouncedFilters.delivery_agent_id) {
        queryParamsObject.deliveryAgentId = debouncedFilters.delivery_agent_id;
      }

      const queryParams = new URLSearchParams(queryParamsObject);

      const result = await API.Api(
        `${DRS_STATUS_REPORT_2}?${queryParams.toString()}`,
      ).get();

      const status = result?.response?.status;
      const response = result?.response?.data;
      const responsePageSize = response?.pagination?.pageSize;
      const responseTotalRecords = response?.pagination?.totalRecords;

      if (status === 200) {
        setTableData(response.data || []);
        setTotal(responseTotalRecords || 0);
        setReturnedRecords(
          (responsePageSize < responseTotalRecords ? responsePageSize : responseTotalRecords) ?? 0
        );
      } else {
        setTableData([]);
        setTotal(0);
        message.error(response?.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching DRS Status Report:", error);
      message.error("Error fetching report data");
      setTableData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (tableData.length === 0) {
      message.error("No data available to download");
      return;
    }
    const queryParamsObject = {
      fromDate_drs_creation_date: dateRange.startDate.format("YYYY-MM-DD"),
      toDate_drs_creation_date: dateRange.endDate.format("YYYY-MM-DD"),
    };

    if (isCPUser && partnerInfo.value) {
      queryParamsObject[partnerInfo.downloadKey] = partnerInfo.value;
    } else if (debouncedFilters.cp_id) {
      queryParamsObject.cpId = debouncedFilters.cp_id;
    }

    if (debouncedFilters.delivery_agent_id) {
      queryParamsObject.deliveryAgentId = debouncedFilters.delivery_agent_id;
    }

    const queryParams = new URLSearchParams(queryParamsObject);
    const downloadUrl = `${DRS_STATUS_REPORT_DOWNLOAD}?${queryParams.toString()}`;
    window.open(downloadUrl, "_blank");
  };

  const fetchChannelPartners = async () => {
    try {
      const result = await API.Api(DRS_STATUS_CHANNEL_PARTNER_LIST).get();
      const response = result?.response?.data;

      if (result?.status && response) {
        setChannelPartners(Array.isArray(response) ? response : (response.data || []));
      } else {
        message.error(
          response?.message ||
            "Failed to fetch DRS channel partner list for filters",
        );
      }
    } catch (error) {
      console.error("Error fetching DRS channel partners:", error);
    }
  };

  const fetchDeliveryAgents = async () => {
    try {
      const result = await API.Api(DRS_STATUS_DELIVERY_AGENT_LIST).get();
      const response = result?.response?.data;

      if (result?.status && response) {
        setDeliveryAgents(Array.isArray(response) ? response : (response.data || []));
      } else {
        message.error(
          response?.message ||
            "Failed to fetch DRS delivery agents list for filters",
        );
      }
    } catch (error) {
      console.error("Error fetching DRS delivery agents:", error);
    }
  };

  useEffect(() => {
    fetchChannelPartners();
    fetchDeliveryAgents();
    fetchRefreshSchedule();
  }, []);

  const fetchRefreshSchedule = async () => {
    try {
      const res = await API.Api(urls.REFRESH_SCHEDULE_DELIVERY).get();
      if (res?.response?.data?.data?.[0]) {
        setRefreshSchedule(res.response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching refresh schedule:", error);
    }
  };

  useEffect(() => {
    fetchReportData(pageNum, pageSize);
  }, [pageNum, pageSize, dateRange, debouncedFilters]);

  const handleDateChange = (dates) => {
    if (dates) {
      setDateRange({
        startDate: dates[0],
        endDate: dates[1],
      });
      setPageNum(1);
    }
  };

  const staticColumns = [
    {
      title: "DRS Creation Date",
      dataIndex: "drs_creation_date",
      key: "drs_creation_date",
      width: 180,
      render: (text) => (text ? moment(text).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Delivered",
      dataIndex: "delivered",
      key: "delivered",
      width: 100,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const drsCreatedDate = record.drs_creation_date
                ? moment(record.drs_creation_date).format("YYYY-MM-DD")
                : "";
              const queryParamsObject = {
                drsNumber: record.new_drs_number,
                type: "delivered",
                drsCreatedDate: drsCreatedDate,
              };
              if (filters.cp_id) queryParamsObject.cp_id = filters.cp_id;
              if (filters.delivery_agent_id)
                queryParamsObject.delivery_agent_id = filters.delivery_agent_id;
              const searchParams = new URLSearchParams(queryParamsObject);
              const url = `/drs-clickable-detail?${searchParams.toString()}`;
              window.open(url, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "RTO Delivered",
      dataIndex: "rto_delivered",
      key: "rto_delivered",
      width: 120,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const drsCreatedDate = record.drs_creation_date
                ? moment(record.drs_creation_date).format("YYYY-MM-DD")
                : "";
              const queryParamsObject = {
                drsNumber: record.new_drs_number,
                type: "rto-delivered",
                drsCreatedDate: drsCreatedDate,
              };
              if (filters.cp_id) queryParamsObject.cp_id = filters.cp_id;
              if (filters.delivery_agent_id)
                queryParamsObject.delivery_agent_id = filters.delivery_agent_id;
              const searchParams = new URLSearchParams(queryParamsObject);
              const url = `/drs-clickable-detail?${searchParams.toString()}`;
              window.open(url, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },

    {
      title: "POD Updated",
      dataIndex: "pod_updated",
      key: "pod_updated",
      width: 120,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const drsCreatedDate = record.drs_creation_date
                ? moment(record.drs_creation_date).format("YYYY-MM-DD")
                : "";
              const queryParamsObject = {
                drsNumber: record.new_drs_number,
                type: "pod-updated",
                drsCreatedDate: drsCreatedDate,
              };
              if (filters.cp_id) queryParamsObject.cp_id = filters.cp_id;
              if (filters.delivery_agent_id)
                queryParamsObject.delivery_agent_id = filters.delivery_agent_id;
              const searchParams = new URLSearchParams(queryParamsObject);
              const url = `/drs-clickable-detail?${searchParams.toString()}`;
              window.open(url, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "POD Update Pending",
      dataIndex: "pod_update_pending",
      key: "pod_update_pending",
      width: 150,
      render: (text, record) => {
        if (text === 0 || text === "0") return text;
        return (
          <span
            style={{ color: "#1890ff", cursor: "pointer", fontWeight: "500" }}
            onClick={() => {
              const drsCreatedDate = record.drs_creation_date
                ? moment(record.drs_creation_date).format("YYYY-MM-DD")
                : "";
              const queryParamsObject = {
                drsNumber: record.new_drs_number,
                type: "pod-pending",
                drsCreatedDate: drsCreatedDate,
              };
              if (filters.cp_id) queryParamsObject.cp_id = filters.cp_id;
              if (filters.delivery_agent_id)
                queryParamsObject.delivery_agent_id = filters.delivery_agent_id;
              const searchParams = new URLSearchParams(queryParamsObject);
              const url = `/drs-clickable-detail?${searchParams.toString()}`;
              window.open(url, "_blank");
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "POD Update Percentage",
      dataIndex: "pod_update_percentage",
      key: "pod_update_percentage",
      width: 150,
      render: (text) => (text ? `${text}%` : "-"),
    },
  ];

  const columns = React.useMemo(
    () => generateDynamicColumns(tableData, staticColumns),
    [tableData, filters],
  );

  return (
    <div className="drs-status-report-container">
      {/* ── Page Top Bar ── */}
      <div className="page-topbar">
        <div className="topbar-left">
          <span className="title-main">DRS Status Report</span>
          {refreshSchedule && (
            <div className="sync-indicators">
              <div className="sync-tag last-refresh">
                <span className="sync-label">Last Refresh</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_last_completion_time_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
              <div className="sync-tag next-sync">
                <span className="sync-label">Next Sync</span>
                <span className="sync-time">
                  {moment(refreshSchedule.dag_next_run_ist).format(
                    "DD-MM-YYYY | HH:mm:ss",
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="topbar-actions">
          <div className="filter-item">
            <span className="filter-label">DRS Creation Date</span>
            <RangePicker
              value={[dateRange.startDate, dateRange.endDate]}
              format={dateFormat_dmy}
              onChange={handleDateChange}
              disabledDate={disabledDate}
              allowClear={false}
            />
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            className="download-btn"
          >
            Download
          </Button>
        </div>
      </div>

      {/* ── Summary Hero Band ── */}
      <div className="summary-hero-band">
        <div className="hero-left">
          <div className="hero-icon">
            <FileTextOutlined />
          </div>
          <div className="hero-text">
            <div className="hero-label">Status Overview</div>
            <div className="hero-desc">
              Monitoring real-time delivery status and POD synchronization
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof total === "number" ? total.toLocaleString() : total}
            </div>
            <div className="hero-stat-label">Total Records</div>
          </div>
          <div className="hero-divider" />
          <div className="hero-stat">
            <div className="hero-stat-value">
              {typeof returnedRecords === "number"
                ? returnedRecords.toLocaleString()
                : returnedRecords}
            </div>
            <div className="hero-stat-label">Showing</div>
          </div>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      <div className="filters-panel">
        <div className="filters-header">
          <FilterOutlined className="filter-icon" />
          <span className="filters-title">Data Filters</span>
        </div>
        <div className="filters-body">
          <Row gutter={[16, 14]}>
            {!isCPUser && (
              <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
                <span className="filter-field-label">Channel Partner</span>
                <Select
                  allowClear
                  showSearch
                  placeholder="Select channel partner"
                  style={{ width: "100%" }}
                  value={filters.cp_id}
                  onChange={(value) => handleFilterChange("cp_id", value)}
                  optionFilterProp="children"
                >
                  {channelPartners.map((cp, idx) => {
                    const label =
                      cp?.channel_partner || (typeof cp === "string" ? cp : "");
                    const value =
                      cp?.cp_id || (typeof cp === "string" ? cp : idx);
                    return (
                      <Option key={value || idx} value={value}>
                        {String(label || value || idx)}
                      </Option>
                    );
                  })}
                </Select>
              </Col>
            )}
            <Col xs={24} sm={12} md={8} lg={6} className="filter-col">
              <span className="filter-field-label">Delivery Agent</span>
              <Select
                allowClear
                showSearch
                placeholder="Select delivery agent"
                style={{ width: "100%" }}
                value={filters.delivery_agent_id}
                onChange={(value) =>
                  handleFilterChange("delivery_agent_id", value)
                }
                optionFilterProp="children"
              >
                {deliveryAgents.map((agent, idx) => {
                  const label = agent?.delivery_agent || (typeof agent === "string" ? agent : "");
                  const value = agent?.delivery_agent_id || (typeof agent === "string" ? agent : idx);
                  return (
                    <Option key={value || idx} value={value}>
                      {String(label || value || idx)}
                    </Option>
                  );
                })}
              </Select>
            </Col>
          </Row>
        </div>
      </div>

      {/* ── Data Table ── */}
      <Spin spinning={loading}>
        <div className="table-panel">
          <TableContainer
            columns={columns}
            dataSource={tableData}
            pagination={{
              // convert to 1-based pagination for UI
              current: pageNum + 1,
              pageSize: pageSize,
              total: typeof total === "number" ? total : 0,
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "150", "200"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(pagination) => handlePageChange(
              pagination.current - 1, // convert back to 0-based for API call
              pagination.pageSize
            )}
            scroll={{ x: "max-content" }}
            rowKey={(record) => record.new_drs_number || Math.random()}
          />
        </div>
      </Spin>
    </div>
  );
};

export default DRSStatusReport;
